import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/auth-context';
import { formatToZMW, rootUrl } from '../../helpers';
import { FaArrowAltCircleLeft, FaCheckCircle, FaCircle, FaTrash } from 'react-icons/fa';
import { BiPlusCircle } from 'react-icons/bi';

const AddBillModal = ({ data, onSubmit, type }) => {
        const auth = useAuth();
        const [lastType, setLastType] = useState(null);
        const [expense, setExpense] = useState(null);
        const [projects, setProjects] = useState([]);
        const [typeOptions, setTypeOptions] = useState([
            { value: "1", label: "Payment" },
            { value: "2", label: "Invoice" },
        ]);
        const [neededAmount, setNeededAmount] = useState('')
        
        const [isDis, setIsDis] = useState(projects.length>0)
        const [entry, setEntry] = useState({
            uniId: null,
            project: null,
            amount: '',
            desc: '',
        });
        const [entriesList, setEntriesList] = useState([]);
        const today = new Date().toISOString().split('T')[0];
        const initialFormData = {
            customer: null,
            sof: {label:'Local', value:1},
            colce: null,
            bank: {
                label:data.bank.find(a=>a.type == 1)?.name,
                value:data.bank.find(a=>a.type == 1)?.id
            },
            invoice: null,
            date: today,
            entries: [],
        };
    
        const [formData, setFormData] = useState(initialFormData);
        const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    // New state for validation errors
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const handleEntryChange = (field, value) => {
        setEntry((prev) => ({ ...prev, [field]: value }));
    };

      const handleAddToList = () => {
            if (entry.uniId && entry.amount && entry.desc) {
                setEntriesList((prev) => [...prev, entry]);
                setEntry({ uniId: null, project: null, amount: '', desc: '' });
            } else {
                toast.info('Please fill all fields before adding to the list.');
            }
        };

    // Mobile-first styles
    const inputStyle = "w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all";
    const errorStyle = "text-red-500 text-sm mt-1";
    const labelStyle = "block text-sm font-medium text-gray-700 mb-1";

    // Enhanced validation
    const validateForm = () => {
        const newErrors = {};
        
        // Form validation
        if (!formData.customer) newErrors.customer = 'Customer/Supplier is required';
        if (!formData.sof) newErrors.sof = 'Source of funds is required';
        if (!formData.colce) newErrors.colce = 'Collection center is required';
        if (!formData.bank) newErrors.bank = 'Bank is required';
        if (entriesList.length === 0) newErrors.entries = 'At least one entry is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
            setIsSubmitDisabled(
                !formData.customer ||
                !formData.sof ||
                !formData.colce ||
                !formData.bank ||
                entriesList.length === 0)
        },[formData, entriesList])

    // Enhanced submit handler
    const handleFormSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fix form errors');
            return;
        }

        setIsSubmitDisabled(true);
        try {
            const payload = { ...formData, entries: entriesList };
            await onSubmit(payload);
            // toast.success('Submission successful!');
        } catch (error) {
            toast.error('Submission failed: ' + error.message);
        } finally {
            setIsSubmitDisabled(false);
        }
    };

    // Mobile-optimized select styles
    const selectStyles = {
        control: (base, { isFocused }) => ({
            ...base,
            minHeight: '48px',
            borderRadius: '0.5rem',
            borderColor: errors[0] ? '#ef4444' : isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
            '&:hover': {
                borderColor: '#3b82f6'
            }
        }),
        option: (base, { isSelected }) => ({
            ...base,
            backgroundColor: isSelected ? '#eff6ff' : 'white',
            color: isSelected ? '#1d4ed8' : '#1f2937',
            '&:hover': {
                backgroundColor: '#f3f4f6'
            }
        })
    };

    const chtData = data.budget.filter(a=>a.coa.substring(0,1) == 1)
    

    const getOptionDetails = (option) => {
                            const coaDetails = data.coa?.find((a) => a.account === option.coa) || {};
                            return {
                                value: option.id,
                                label: `${option.coa} - 
                                ${type === 2?coaDetails.name || '':''} -
                                ${data.program.find(a=>a.programCode == option.program)?.name  || ''} -
                                ${data.subProgram.find(a=>parseFloat(a.subProgramCode) == parseFloat(option.subprogram)
                                && a.programCode == option.program
                                )?.name  || ''} -
                                ${data.dept.find(a=>a.id == option.dept)?.name  || ''}
                                `
                            };
                        };

    return (
        <div className="p-4 bg-white rounded-lg shadow-sm max-h-[90vh] overflow-y-auto">
            <div className="space-y-6">
                {/* Form Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900"> Receipt </h3>
        <hr />
                    {/* Budget Selection */}
                    <div>
                        <label className={labelStyle}>Budget Item</label>
                        <Select
                            styles={selectStyles}
                            options={chtData.map(getOptionDetails)}
                            placeholder="Search budget items..."
                            onChange={(selected) => {
                                handleEntryChange('uniId', selected);
                                setTouched(prev => ({ ...prev, uniId: true }));
                            }}
                            value={entry.uniId}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isClearable
                        />
                        {touched.uniId && errors.uniId && (
                            <div className={errorStyle}>{errors.uniId}</div>
                        )}
                    </div>

                    {/* Project Selection */}
                    <div>
                        <label className={labelStyle}>Project</label>
                        <Select
                            styles={selectStyles}
                            options={projects.map((project) => ({
                                value: project.id,
                                label: `${project.sub} - ${project.name}`,
                            }))}
                            placeholder="Select Project"
                            onChange={(selected) => handleEntryChange('project', selected)}
                            value={entry.project}
                            isDisabled={isDis}
                            isClearable
                        />
                    </div>

                    {/* Amount Input */}
                    <div>
                        <label className={labelStyle}>Amount</label>
                        <div className="relative">
                            <input
                                type="number"
                                placeholder="0.00"
                                className={`${inputStyle} ${errors.amount ? 'border-red-500' : ''}`}
                                value={entry.amount}
                                onChange={(e) => {
                                    handleEntryChange('amount', e.target.value);
                                    setTouched(prev => ({ ...prev, amount: true }));
                                }}
                                min="0"
                                step="0.01"
                            />
                            {errors.amount && (
                                <FaCircle className="w-5 h-5 text-red-500 absolute right-3 top-3.5" />
                            )}
                        </div>
                        <div className="flex justify-between mt-1">
                            {errors.amount ? (
                                <span className={errorStyle}>{errors.amount}</span>
                            ) : (
                                <span className="text-sm text-gray-500">
                                    Budget: {formatToZMW(neededAmount)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Description Input */}
                    <div>
                        <label className={labelStyle}>Description</label>
                        <textarea
                            placeholder="Enter description..."
                            className={`${inputStyle} ${errors.desc ? 'border-red-500' : ''}`}
                            rows={2}
                            value={entry.desc}
                            onChange={(e) => {
                                handleEntryChange('desc', e.target.value);
                                setTouched(prev => ({ ...prev, desc: true }));
                            }}
                        />
                        {errors.desc && (
                            <div className={errorStyle}>{errors.desc}</div>
                        )}
                    </div>

                    {/* Add Entry Button */}
                    <button
                        onClick={handleAddToList}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg flex items-center justify-center gap-2 transition-all"
                    >
                        <BiPlusCircle className="w-5 h-5" />
                        Add Entry
                    </button>
                </div>

                {/* Entries List - Mobile Optimized */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Added Entries</h3>
                    
                    {entriesList.length > 0 ? (
                        <div className="space-y-3">
                            {entriesList.map((item, index) => (
                                <div key={index} className="bg-gray-50 p-4 rounded-lg relative">
                                    <button
                                        onClick={() => setEntriesList(prev => prev.filter((_, i) => i !== index))}
                                        className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                                    >
                                        <FaTrash className="w-5 h-5" />
                                    </button>
                                    
                                    <div className="space-y-2">
                                        <div>
                                            <span className="text-sm font-medium">Budget:</span>
                                            <p className="text-sm text-gray-700">{item.uniId.label}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium">Amount:</span>
                                            <p className="text-sm text-gray-700">{item.amount}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium">Description:</span>
                                            <p className="text-sm text-gray-700">{item.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 text-sm">No entries added yet</p>
                        </div>
                    )}
                </div>

                {/* Submission Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Finalize Receipt</h3>
                    
                    {/* Form Fields - Stacked for Mobile */}
                    <div>
                    
                    <Select
                        options={
                                data.suppliers.filter(a=>a.type == 3).map((option) => ({
                                      value: option.id,
                                      label: `${option.name}`,
                                  }))  
                        }
                        placeholder={type === 2 ? "Select Supplier" : "Select Customer"}
                        onChange={(selected) =>
                            setFormData({ ...formData, customer: selected })
                        }
                        value={formData.customer}
                        className="mb-4"
                    />
                    <Select
                        options={data.sof.map((option) => ({
                            value: option.id,
                            label: `${option.source_of_fund}`,
                        }))}
                        placeholder="Source of Funds"
                        onChange={(selected) =>
                            setFormData({ ...formData, sof: selected })
                        }
                        value={{label:'Local', value:1}}
                        className="mb-4"
                        isClearable
                        isDisabled
                    />
                    <Select
                        options={data.cc.map((option) => ({
                            value: option.id,
                            label: `${option.collection_name}`,
                        }))}
                        placeholder="Select Collection Center"
                        onChange={(selected) =>
                            setFormData({ ...formData, colce: selected })
                        }
                        value={formData.colce}
                        className="mb-4"
                        isClearable
                    />
                    <Select
                        options={data.bank.map((option) => ({
                            value: option.id,
                            label: `${option.name}`,
                        }))}
                        placeholder="Select Bank"
                        onChange={(selected) =>
                            setFormData({ ...formData, bank: selected })
                        }
                        value={formData.bank}
                        className="mb-4"
                        isClearable
                       
                    />
                    {
                        type === 2 && data.suppliers.find(a=>a.id == formData.customer?.value)?.type == 1?
                        <Select
                            options={[411520, 411140, 411150].map((option) => ({
                                value: option,
                                label: option,
                            }))}
                            placeholder="Select expense type"
                            onChange={(selected) => setExpense(selected)}
                            value={
                                expense
                            }
                            className="mb-4"
                            isClearable
                        />
                    :null
                    }
                    <input
                        type="text"
                        placeholder="Cheque # / Reference"
                        className="border rounded p-2 w-full mb-4"
                        value={formData.invoice}
                        onChange={(e) =>
                            setFormData({ ...formData, invoice: e.target.value })
                        }
                    />
                    <input
                        type="date"
                        placeholder="Date"
                        className="border rounded p-2 w-full mb-4"
                        value={formData.date}
                        onChange={(e) =>
                            setFormData({ ...formData, date: e.target.value })
                        }
                        disabled={true}
                    />
                    
                </div>

                    {/* Submit Button */}
                    <button
                        onClick={handleFormSubmit}
                        disabled={isSubmitDisabled}
                        className={`w-full p-3 rounded-lg flex items-center justify-center gap-2 transition-all ${
                            isSubmitDisabled 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {isSubmitDisabled ? (
                            <>
                                <FaCircle className="w-5 h-5" />
                                Missing Information
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="w-5 h-5" />
                                Submit Receipt
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddBillModal;